// Web Crypto utilities for secure encryption

// Convert string to ArrayBuffer
export const stringToBuffer = (str) => {
  return new TextEncoder().encode(str);
};

// Convert ArrayBuffer to string
export const bufferToString = (buffer) => {
  return new TextDecoder().decode(buffer);
};

// Convert ArrayBuffer to base64
export const bufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

// Convert base64 to ArrayBuffer
export const base64ToBuffer = (base64) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

// Generate random salt
export const generateSalt = () => {
  return crypto.getRandomValues(new Uint8Array(16));
};

// Derive encryption key from passcode using PBKDF2
export const deriveKey = async (passcode, salt) => {
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    stringToBuffer(passcode),
    { name: 'PBKDF2' },
    false,
    ['deriveBits', 'deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt,
      iterations: 100000,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
};

// Encrypt data using AES-GCM
export const encryptData = async (data, key) => {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encodedData = stringToBuffer(JSON.stringify(data));

  const encryptedData = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: iv,
    },
    key,
    encodedData
  );

  return {
    encrypted: bufferToBase64(encryptedData),
    iv: bufferToBase64(iv),
  };
};

// Decrypt data using AES-GCM
export const decryptData = async (encryptedObj, key) => {
  const iv = base64ToBuffer(encryptedObj.iv);
  const encryptedData = base64ToBuffer(encryptedObj.encrypted);

  const decryptedData = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: iv,
    },
    key,
    encryptedData
  );

  return JSON.parse(bufferToString(decryptedData));
};